<template>
  <div :dir="isRtl !== false ? 'rtl' : 'ltr'" class="relative agent-tone-selector-container" :style="containerStyle">
    <button
      class="btn relative w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0 agent-tone-selector-button"
      type="button"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedToneObject !== null" class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0" style="margin-inline-end: 0.75rem;">
          <Icon :icon="selectedToneObject.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-1 min-w-0 overflow-hidden relative" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full">{{ selectedToneObject.displayName }}</span>
          <span class="block text-xs text-base-content opacity-70 truncate">{{ selectedToneObject.description }}</span>
        </div>
      </div>
      <div v-else class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0" style="margin-inline-end: 0.75rem;">
          <Icon icon="heroicons:chat-bubble-bottom-center-text-20-solid" class="w-4 h-4 text-base-content" />
        </span>
        <span class="text-sm text-base-content opacity-70 whitespace-nowrap">{{ placeholderLabel }}</span>
      </div>
      <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 absolute top-1/2 -translate-y-1/2" :class="isRtl === true ? 'left-3' : 'right-3'" />
    </button>
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen === true"
        class="absolute z-20 mt-1 bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 agent-tone-selector-dropdown"
        :style="dropdownStyle"
        ref="dropdownListRef"
      >
        <div class="py-1 space-y-1">
          <div
            v-for="tone in toneOptions"
            :key="tone.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{ 
              'bg-primary text-primary-content': tone.id === selectedTone,
              'hover:bg-base-200 dark:hover:bg-gray-700': tone.id !== selectedTone
            }"
            @click="selectToneAndClose(tone)"
          >
            <div class="p-3">
              <div class="flex items-start" :class="isRtl === true ? 'flex-row-reverse' : ''">
                <div class="flex-shrink-0">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600">
                    <Icon :icon="tone.icon" class="w-5 h-5 text-base-content" :class="{'text-primary-content': tone.id === selectedTone}" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'mr-4 text-right' : 'ml-4 text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="[{ 'text-primary-content': tone.id === selectedTone }, isRtl === true ? 'text-right' : 'text-left']">{{ tone.displayName }}</h3>
                  <p class="text-xs mt-0.5" :class="[tone.id === selectedTone ? 'text-primary-content opacity-80' : 'text-base-content opacity-70', isRtl === true ? 'text-right' : 'text-left']">
                    {{ tone.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import toneDefinitions from '../shared/agentTones.json';

const props = defineProps({
  selectedTone: {
    type: String,
    default: 'default'
  },
  closedWidth: {
    type: String,
    default: '11.5rem'
  },
  openWidth: {
    type: String,
    default: '16rem'
  }
});

const emit = defineEmits(['update:selectedTone', 'tone-change-requested']);

const { t, currentLanguage } = useI18n();
const isOpen = ref(false);
const dropdownListRef = ref(null);
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

const toneEntries = computed(() => {
  return (Array.isArray(toneDefinitions) === true ? toneDefinitions : []).map((definition) => {
    const displayName = t(`agentToneSelector.tones.${definition.id}.displayName`, definition.defaultDisplayName);
    const description = t(`agentToneSelector.tones.${definition.id}.description`, definition.defaultDescription);
    const prompt = t(`agentToneSelector.prompts.${definition.id}`, definition.prompt);
    return {
      ...definition,
      displayName,
      description,
      prompt
    };
  });
});

const toneOptions = toneEntries;
const placeholderLabel = computed(() => t('agentToneSelector.selectTone', 'Select tone'));

const selectedToneObject = computed(() => toneOptions.value.find(option => option.id === props.selectedTone) ?? null);
const containerStyle = computed(() => ({
  width: isOpen.value === true ? props.openWidth : props.closedWidth,
  transition: 'width 0.2s ease'
}));
const dropdownStyle = computed(() => ({
  width: isOpen.value === true ? props.openWidth : props.closedWidth,
  transition: 'width 0.2s ease'
}));
const arrowSpacingClass = computed(() => (isRtl.value === true ? 'mr-auto' : 'ml-auto'));
const textAlignmentClass = computed(() => (isRtl.value === true ? 'text-right' : 'text-left'));

const toggleDropdown = () => {
  isOpen.value = isOpen.value === false;
};

const selectToneAndClose = (tone) => {
  if (props.selectedTone !== tone.id) {
    emit('update:selectedTone', tone.id);
    emit('tone-change-requested', tone.id);
  }
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  const target = event.target;
  if (dropdownListRef.value !== null && isOpen.value === true) {
    if (dropdownListRef.value.contains(target) === false) {
      const button = target.closest('.agent-tone-selector-button');
      if (button === null) {
        isOpen.value = false;
      }
    }
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});
</script>

<style scoped>
.agent-tone-selector-container {
  transition: width 0.2s ease;
}
.agent-tone-selector-dropdown {
  transition: width 0.2s ease;
}
.icon-spacing {
  margin-inline-end: 12px;
}
</style>

